import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  Group,
  Loader,
  Paper,
  Radio,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { IconFileTypePdf, IconInfoCircle, IconFile } from "@tabler/icons-react";
import dayjs from "dayjs";
import useTransientStore from "../../../data/transcientStore";
import { useTranslation } from "../../../hooks/useTranslation";
import { usePermissions } from "../../../hooks/usePermissions";
import {
  downloadCityObjectPdf,
  downloadPtrFile,
  searchCityObjectPrintouts,
} from "../../../services/CityObjectPrintoutService";
import { getContractStatuses } from "../../../services/ContractDefaultUsersService";
import {
  getContractsByAddressSystemAndUser,
  getPrintoutSearchForemen,
  getQualityControlByAddressSystem,
  getStatusByAddressSystem,
  getSubscribersByAddressSystem,
  getTypeByAddressSystem,
} from "../../../services/MapComponentsService";
import type { ContractListItem } from "../../../types/UserRole";
import type { ContractStatusDropdownItem } from "../../../types/ContractDefaultUsers";
import type { GeometryStatus, QualityControl } from "../../../types/BaseGeometry";
import type { CityObjectPrintoutSearchResultRowType } from "../../../types/CityObjectPrintout";
import { notifications } from "@mantine/notifications";

const DATE_FMT = "DD.MM.YYYY";
const ISO = "YYYY-MM-DD";

function formatSpvm(iso: string | null): string {
  if (!iso) return "";
  const d = dayjs(iso);
  return d.isValid() ? d.format(DATE_FMT) : "";
}

function statusDotColor(
  stateId: number,
  byId: Map<number, GeometryStatus>,
): string {
  const s = byId.get(stateId);
  const c = s?.colour;
  if (c && c.length >= 3) {
    return `rgb(${c[0]}, ${c[1]}, ${c[2]})`;
  }
  return "var(--mantine-color-gray-5)";
}

export default function CityObjectPrintoutsSearchPage() {
  const selectedAddressSystem = useTransientStore((s) => s.selectedAddressSystem);
  const currentUserId = useTransientStore((s) => s.currentUserId);
  const { checkAccess } = usePermissions();
  const addressSystemId = selectedAddressSystem?.id ?? 0;
  const userId = currentUserId ?? 0;

  const { getTranslation } = useTranslation({
    pageId: 512,
    addressSystemId,
    autoLoad: true,
  });

  const canUse = checkAccess("CITY_OBJECT_SHOW");

  const [loadingMeta, setLoadingMeta] = useState(true);
  const [contractStatuses, setContractStatuses] = useState<
    ContractStatusDropdownItem[]
  >([]);
  const [contractStatusId, setContractStatusId] = useState<number | null>(null);
  const [allContracts, setAllContracts] = useState<ContractListItem[]>([]);
  const [selectedContractIds, setSelectedContractIds] = useState<number[]>([]);
  const [subscribers, setSubscribers] = useState<{ id: number; name: string }[]>(
    [],
  );
  const [foremen, setForemen] = useState<{ id: number; name: string }[]>([]);
  const [statuses, setStatuses] = useState<GeometryStatus[]>([]);
  const [types, setTypes] = useState<GeometryStatus[]>([]);
  const [qualityControls, setQualityControls] = useState<QualityControl[]>([]);

  const [location, setLocation] = useState("");
  const [foremanId, setForemanId] = useState<string | null>("");
  const [statusId, setStatusId] = useState<string | null>("");
  const [typeId, setTypeId] = useState<string | null>("");
  const [qcId, setQcId] = useState<string | null>("");
  const [subscriberId, setSubscriberId] = useState<string | null>("");
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [sortByName, setSortByName] = useState(false);

  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<CityObjectPrintoutSearchResultRowType[]>(
    [],
  );
  const [hasSearched, setHasSearched] = useState(false);

  const statusById = useMemo(() => {
    const m = new Map<number, GeometryStatus>();
    statuses.forEach((s) => m.set(s.id, s));
    return m;
  }, [statuses]);

  // Only filter by status when the user can pick among 2+ statuses. If there is 0–1 lookup
  // row, the dropdown is hidden but we used to still set contractStatusId — that hid every
  // contract whose DB status did not match that single id.
  const filteredContracts = useMemo(() => {
    if (contractStatuses.length <= 1 || contractStatusId == null) return allContracts;
    return allContracts.filter(
      (c) =>
        c.contractStatusId == null || c.contractStatusId === contractStatusId,
    );
  }, [allContracts, contractStatusId, contractStatuses.length]);

  const loadMeta = useCallback(async () => {
    if (addressSystemId <= 0 || userId <= 0) {
      setLoadingMeta(false);
      return;
    }
    setLoadingMeta(true);
    try {
      const [st, contractsRaw, sub, fm, stLookup, ty, qc] = await Promise.all([
        getContractStatuses(addressSystemId),
        getContractsByAddressSystemAndUser(addressSystemId, userId),
        getSubscribersByAddressSystem(addressSystemId),
        getPrintoutSearchForemen(addressSystemId, userId),
        getStatusByAddressSystem(addressSystemId),
        getTypeByAddressSystem(addressSystemId),
        getQualityControlByAddressSystem(addressSystemId),
      ]);

      setContractStatuses(st);
      const def = st.find((x) => x.defaultValue === 1)?.id ?? st[0]?.id ?? null;
      setContractStatusId(def);

      setAllContracts(contractsRaw);
      setSubscribers(
        (sub as { id: number; name: string }[]).map((s) => ({
          id: s.id,
          name: s.name,
        })),
      );
      setForemen(fm);
      setStatuses(stLookup);
      setTypes(ty as GeometryStatus[]);
      setQualityControls(qc as QualityControl[]);
    } catch (e) {
      console.error(e);
      notifications.show({
        title: getTranslation("REPORTS_ERROR") || "Error",
        message:
          e instanceof Error ? e.message : "Failed to load printout search data",
        color: "red",
      });
    } finally {
      setLoadingMeta(false);
    }
  }, [addressSystemId, userId, getTranslation]);

  useEffect(() => {
    void loadMeta();
  }, [loadMeta]);

  useEffect(() => {
    setSelectedContractIds(filteredContracts.map((c) => c.id));
  }, [filteredContracts]);

  const toggleContract = (id: number, checked: boolean) => {
    setSelectedContractIds((prev) => {
      if (checked) return prev.includes(id) ? prev : [...prev, id];
      return prev.filter((x) => x !== id);
    });
  };

  const selectAllContracts = () =>
    setSelectedContractIds(filteredContracts.map((c) => c.id));
  const clearContracts = () => setSelectedContractIds([]);

  const parseSel = (v: string | null) =>
    v && v !== "" ? Number.parseInt(v, 10) : null;

  const handleSearch = async () => {
    if (addressSystemId <= 0) return;
    if (selectedContractIds.length === 0) {
      notifications.show({
        message:
          getTranslation("PRINTOUTS_SELECT_CONTRACT") ||
          "Select at least one contract.",
        color: "yellow",
      });
      return;
    }
    setSearching(true);
    setResults([]);
    try {
      const res = await searchCityObjectPrintouts({
        addressSystemId,
        currentUserId: userId,
        contractIds: selectedContractIds,
        locationContains: location.trim() || null,
        foremanId: parseSel(foremanId),
        statusId: parseSel(statusId),
        strengthTypeId: parseSel(typeId),
        qualityControlId: parseSel(qcId),
        subscriberId: parseSel(subscriberId),
        plannedDateFrom: dateFrom ? dayjs(dateFrom).format(ISO) : null,
        plannedDateTo: dateTo ? dayjs(dateTo).format(ISO) : null,
        sortByName,
      });
      setResults(res.rows);
      setHasSearched(true);
    } catch (e) {
      console.error(e);
      notifications.show({
        title: getTranslation("REPORTS_ERROR") || "Error",
        message:
          e instanceof Error ? e.message : "Search failed",
        color: "red",
      });
    } finally {
      setSearching(false);
    }
  };

  const allLabel = getTranslation("PRINTOUTS_ALL") || "All";

  if (!canUse) {
    return (
      <Alert color="yellow" title={getTranslation("PRINTOUTS_NO_RIGHTS") || "Access"}>
        {getTranslation("PRINTOUTS_NO_RIGHTS_DETAIL") ||
          "You need CITY_OBJECT_SHOW to use printout search."}
      </Alert>
    );
  }

  if (addressSystemId <= 0 || userId <= 0) {
    return (
      <Text c="dimmed" size="sm">
        {getTranslation("PRINTOUTS_SELECT_CONTEXT") ||
          "Select an address system and sign in."}
      </Text>
    );
  }

  if (loadingMeta) {
    return (
      <Group justify="center" p="md">
        <Loader />
      </Group>
    );
  }

  return (
    <Stack gap="md">
      <Stack gap="sm">
        {contractStatuses.length > 1 && (
          <SelectRow
            label={
              getTranslation("TEXT_LABEL_CONTRACT_STATUS") || "Contract status"
            }
            value={contractStatusId?.toString() ?? ""}
            onChange={(v) => setContractStatusId(v ? Number(v) : null)}
            data={contractStatuses.map((c) => ({
              value: c.id.toString(),
              label: c.name,
            }))}
          />
        )}

        <Box>
          <Text size="sm" fw={600} mb={4}>
            {getTranslation("TEXT_LABEL_SOPIMUKSET") || "Contracts"}
          </Text>
          <Group gap="xs" mb="xs">
            <Button size="xs" variant="light" onClick={selectAllContracts}>
              {getTranslation("PRINTOUTS_CHECK_ALL") || "Select all"}
            </Button>
            <Button size="xs" variant="light" onClick={clearContracts}>
              {getTranslation("PRINTOUTS_UNCHECK_ALL") || "Clear"}
            </Button>
          </Group>
          <Stack gap={4}>
            {filteredContracts.map((c) => (
              <Checkbox
                key={c.id}
                label={c.name}
                checked={selectedContractIds.includes(c.id)}
                onChange={(e) => toggleContract(c.id, e.currentTarget.checked)}
              />
            ))}
          </Stack>
          {filteredContracts.length === 0 && (
            <Text size="sm" c="dimmed" mt="xs">
              {allContracts.length === 0
                ? getTranslation("PRINTOUTS_NO_CONTRACTS") ||
                  "No contracts are linked to your user for this address system."
                : getTranslation("PRINTOUTS_NO_CONTRACTS_STATUS") ||
                  "No contracts match the selected contract status. Choose a different status above."}
            </Text>
          )}
        </Box>

        <TextInput
          label={getTranslation("TEXT_LABEL_LOCATION") || "Name / location"}
          value={location}
          onChange={(e) => setLocation(e.currentTarget.value)}
        />

        {subscribers.length > 0 && (
          <SelectRow
            label={getTranslation("TEXT_LABEL_TILAAJA") || "Subscriber"}
            value={subscriberId ?? ""}
            onChange={setSubscriberId}
            data={[
              { value: "", label: allLabel },
              ...subscribers.map((s) => ({
                value: s.id.toString(),
                label: s.name,
              })),
            ]}
          />
        )}

        <SelectRow
          label={getTranslation("TEXT_LABEL_TYONJOHTAJA") || "Foreman"}
          value={foremanId ?? ""}
          onChange={setForemanId}
          data={[
            { value: "", label: allLabel },
            ...foremen.map((f) => ({
              value: f.id.toString(),
              label: f.name,
            })),
          ]}
        />

        <SelectRow
          label={getTranslation("TEXT_LABEL_TILA") || "Status"}
          value={statusId ?? ""}
          onChange={setStatusId}
          data={[
            { value: "", label: allLabel },
            ...statuses.map((s) => ({
              value: s.id.toString(),
              label: s.description || s.name,
            })),
          ]}
        />

        <SelectRow
          label={getTranslation("TEXT_LABEL_VOIMAKKUUS") || "Type"}
          value={typeId ?? ""}
          onChange={setTypeId}
          data={[
            { value: "", label: allLabel },
            ...types.map((s) => ({
              value: s.id.toString(),
              label: s.description || s.name,
            })),
          ]}
        />

        <SelectRow
          label={
            getTranslation("TEXT_LABEL_QUALITY_CONTROL") || "Quality control"
          }
          value={qcId ?? ""}
          onChange={setQcId}
          data={[
            { value: "", label: allLabel },
            ...qualityControls.map((s) => ({
              value: s.id.toString(),
              label: s.description || s.name,
            })),
          ]}
        />

        <Group align="flex-end" wrap="wrap">
          <DateInput
            label={getTranslation("PRINTOUTS_DATE_START") || "Start"}
            value={dateFrom}
            onChange={setDateFrom}
            valueFormat={DATE_FMT}
            clearable
          />
          <DateInput
            label={getTranslation("PRINTOUTS_DATE_END") || "End"}
            value={dateTo}
            onChange={setDateTo}
            valueFormat={DATE_FMT}
            clearable
          />
        </Group>

        <Radio.Group
          label={getTranslation("TEXT_LABEL_JARJESTYS") || "Result order"}
          value={sortByName ? "name" : "spvm"}
          onChange={(v) => setSortByName(v === "name")}
        >
          <Group mt="xs">
            <Radio
              value="spvm"
              label={
                getTranslation("TEXT_LABEL_JARJESTYS_AIKA") || "SPVM (date)"
              }
            />
            <Radio
              value="name"
              label={getTranslation("TEXT_LABEL_JARJESTYS_NIMI") || "Name"}
            />
          </Group>
        </Radio.Group>

        <Button loading={searching} onClick={() => void handleSearch()}>
          {getTranslation("TEXT_LABEL_HAE") || "Search"}
        </Button>
      </Stack>

      {results.length === 0 && !searching && (
        <Text c="dimmed" size="sm">
          {getTranslation("PRINTOUTS_NO_RESULTS_YET") ||
            "Run a search to see results."}
        </Text>
      )}

      {results.length > 0 && (
        <Box mt="xl">
          <Paper withBorder p="md">
            <Title order={6} c="blue" mb="md">
              {getTranslation("LABEL_SEARCH_RESULTS_TITLE") || "Search results"}{" "}
              ({results.length})
            </Title>
            <Stack gap="xs">
              {results.map((row) => (
                <PrintoutSearchResultCard
                  key={row.perceptionId}
                  row={row}
                  statusById={statusById}
                  currentUserId={userId}
                  canMpk={
                    checkAccess("PRICES_SHOW") ||
                    checkAccess("CITY_OBJECT_REPORT_MPK")
                  }
                  getTranslation={getTranslation}
                />
              ))}
            </Stack>
          </Paper>
        </Box>
      )}
    </Stack>
  );
}

function PrintoutSearchResultCard(props: {
  row: CityObjectPrintoutSearchResultRowType;
  statusById: Map<number, GeometryStatus>;
  currentUserId: number;
  canMpk: boolean;
  getTranslation: (key: string) => string | null;
}) {
  const { row, statusById, canMpk, currentUserId, getTranslation } = props;
  const ptrMany = row.binaryFileCount > 1;
  const hasPtr = !ptrMany && !!row.ptrApiPath;
  const showMpkColumn = row.showMpk === 1 && canMpk;

  const handleDownloadPdf = (type: "kk" | "pk" | "mpk") => {
    downloadCityObjectPdf(row.perceptionId, type, currentUserId).catch((err) => {
      console.error(`Failed to download ${type.toUpperCase()} PDF`, err);
      notifications.show({
        title: getTranslation("REPORTS_ERROR") || "Error",
        message: `Failed to download ${type.toUpperCase()} PDF.`,
        color: "red",
      });
    });
  };

  const handleDownloadPtr = () => {
    if (!row.ptrApiPath) return;
    downloadPtrFile(row.ptrApiPath).catch((err) => {
      console.error("Failed to download PTR file", err);
      notifications.show({
        title: getTranslation("REPORTS_ERROR") || "Error",
        message: "Failed to download PTR file.",
        color: "red",
      });
    });
  };

  return (
    <Paper p="sm" withBorder>
      <Stack gap={4}>
        <Group
          justify="space-between"
          wrap="nowrap"
          align="flex-start"
          gap="sm"
        >
          <Text
            fw={600}
            size="md"
            style={{ flex: 1, wordBreak: "break-word", lineHeight: 1.35 }}
          >
            {row.location?.trim() || "—"}
          </Text>
          <Badge size="sm" variant="light" color="blue" style={{ flexShrink: 0 }}>
            #{row.perceptionId}
          </Badge>
        </Group>
        {row.contractShortName?.trim() ? (
          <Text size="xs" style={{ wordBreak: "break-word" }}>
            <b>{getTranslation("LABEL_SEARCH_CONTRACT") || "Contract:"}</b>{" "}
            {row.contractShortName}
          </Text>
        ) : null}
        <Group gap="xs" wrap="nowrap">
          <Text size="xs">
            <b>{getTranslation("TEXT_LABEL_PVM") || "SPVM:"}</b>{" "}
            {formatSpvm(row.plannedDate) || "—"}
          </Text>
        </Group>
        <Group gap={6} wrap="nowrap" align="center">
          <Box
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              flexShrink: 0,
              backgroundColor: statusDotColor(row.stateId, statusById),
              boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.06)",
            }}
          />
          <Text size="xs" style={{ wordBreak: "break-word" }}>
            <b>{getTranslation("TEXT_LABEL_TILA") || "Status:"}</b>{" "}
            {row.statusLabel || "—"}
          </Text>
        </Group>
        <Divider my="xs" />
        <Group gap={6} wrap="wrap">
          <Button
            variant="light"
            size="xs"
            leftSection={<IconFileTypePdf size={14} />}
            onClick={() => handleDownloadPdf("kk")}
          >
            KK
          </Button>
          <Button
            variant="light"
            size="xs"
            leftSection={<IconFileTypePdf size={14} />}
            onClick={() => handleDownloadPdf("pk")}
          >
            PK
          </Button>
          {showMpkColumn ? (
            <Button
              variant="light"
              size="xs"
              leftSection={<IconFileTypePdf size={14} />}
              onClick={() => handleDownloadPdf("mpk")}
            >
              MPK
            </Button>
          ) : (
            <Text size="xs" c="dimmed" component="span">
              MPK —
            </Text>
          )}
          {ptrMany ? (
            <Tooltip
              label={
                getTranslation("PRINTOUTS_PTR_MANY") ||
                `Multiple reports (${row.binaryFileCount}). Open from object card.`
              }
              withArrow
            >
              <Group gap={4} wrap="nowrap" style={{ cursor: "help" }}>
                <IconInfoCircle size={18} color="var(--mantine-color-blue-6)" />
                <Text size="xs" c="blue">
                  PTR
                </Text>
              </Group>
            </Tooltip>
          ) : hasPtr ? (
            <Button
              variant="light"
              size="xs"
              leftSection={<IconFile size={14} />}
              onClick={handleDownloadPtr}
            >
              PTR
            </Button>
          ) : (
            <Text size="xs" c="dimmed" component="span">
              PTR —
            </Text>
          )}
        </Group>
      </Stack>
    </Paper>
  );
}

function SelectRow(props: {
  label: string;
  value: string;
  onChange: (v: string | null) => void;
  data: { value: string; label: string }[];
}) {
  return (
    <Box>
      <Text component="label" size="sm" fw={600} display="block" mb={4}>
        {props.label}
      </Text>
      <select
        className="mantine-Input-input mantine-Select-input"
        style={{
          width: "100%",
          minHeight: 36,
          padding: "0 12px",
          borderRadius: 4,
          border: "1px solid var(--mantine-color-gray-4)",
        }}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value || null)}
      >
        {props.data.map((d) => (
          <option key={d.value || "empty"} value={d.value}>
            {d.label}
          </option>
        ))}
      </select>
    </Box>
  );
}

